<div class="ddp-head">
  <strong>Zone slider data</strong>
  <span class="ddp-head-actions">
    <button type="button" class="ddp-icon" (click)="reload()" matTooltip="Reload datasets and rows" aria-label="Reload data">
      <mat-icon>refresh</mat-icon>
    </button>
  </span>
</div>
<p class="ddp-hint">
  Connecting <b>{{ node().name }}</b>. Map the columns, then <b>Generate Configuration</b> fetches their
  values from the compute API. Anything left unmapped keeps the value set in the Design tab, so the
  slider can be fully static, fully data-driven, or a mix.
</p>

<ng-container [ngSwitch]="data.datasets().status">
  <p class="ddp-status" *ngSwitchCase="'loading'">Loading datasets…</p>
  <p class="ddp-status is-error" *ngSwitchCase="'error'">
    {{ $any(data.datasets()).message }}
    <button type="button" class="ddp-link" (click)="reload()">Retry</button>
  </p>
</ng-container>

<!-- ── Where the columns come from (filled in by itself) ─────────────────── -->
<div class="ddp-field zsd-source">
  <label>Data from <small>{{ autoDataset() ? 'picked from your dashboard' : 'the table the columns below come from' }}</small></label>
  <ng-select
    #dsSelect
    class="ss-select"
    [class.is-open]="!!dsSelect.isOpen()"
    appendTo="body"
    [items]="datasetOptions()"
    [searchable]="true"
    [clearable]="true"
    placeholder="Select a dataset"
    [ngModel]="binding().dataset || null"
    (ngModelChange)="setDataset($event)"
  ></ng-select>
  <p class="ddp-status is-error" *ngIf="columnsError(binding().dataset) as message">{{ message }}</p>
</div>

<!-- ── The handle ─────────────────────────────────────────────────────────── -->
<div class="zsd-block zsd-handle">
  <div class="zsd-row">
      <label>Handle (dragger) <small>where it sits</small></label>
      <div class="zsd-row-pickers">
        <ng-select #hCol class="ss-select" [class.is-open]="!!hCol.isOpen()" appendTo="body"
          [items]="columnsOf(binding().dataset)" [searchable]="true" [clearable]="true" [disabled]="!binding().dataset"
          [placeholder]="columnPlaceholder(binding().dataset)"
          [ngModel]="binding().valueField || null" (ngModelChange)="setField('valueField', $event)"></ng-select>
        <ng-select #hAgg class="ss-select" [class.is-open]="!!hAgg.isOpen()" appendTo="body"
          [items]="data.aggregations()" bindLabel="label" bindValue="value" [clearable]="false" [searchable]="false"
          [disabled]="!binding().valueField"
          [ngModel]="binding().valueAggregation" (ngModelChange)="setField('valueAggregation', $event)"></ng-select>
      </div>
    </div>
  <div class="zsd-row">
      <label>Margin formula <small>shown as &#123;margin&#125;; use column names like [set_price], or [Value] [Cost] [Min] [Max]; run Generate after editing</small></label>
      <input class="zsd-formula" type="text" spellcheck="false"
        placeholder="([Value] - [Cost]) / [Value] * 100"
        [ngModel]="binding().marginFormula" (ngModelChange)="setMarginFormula($event)" />
    </div>
</div>

<!-- ── Which record the slider stands for (for approvals) ─────────────────── -->
<div class="ddp-field zsd-record">
  <label>Record ID column <small>for approvals - the selected spec's id</small></label>
  <ng-select #recCol class="ss-select" [class.is-open]="!!recCol.isOpen()" appendTo="body"
    [items]="recordIdOptions()" [addTag]="true" addTagText="Use column" [searchable]="true" [clearable]="true"
    [disabled]="!binding().dataset" placeholder="e.g. uuid"
    [ngModel]="binding().recordIdField || null" (ngModelChange)="setField('recordIdField', $event)"></ng-select>
  <p class="ddp-status" *ngIf="recordIdNow() as id">Record now: {{ id }}</p>
</div>

<!-- ── Blocks: colour, label, From column, To column ──────────────────────── -->
<h5 class="zsd-section">Blocks</h5>
<ng-container *ngIf="binding().zonesMode === 'columns'; else otherZones">
  <div class="zsd-block" *ngFor="let level of binding().levels; let i = index; let first = first; let last = last; trackBy: trackByIndex">
    <div class="zsd-level-head">
      <input type="color" class="zsd-level-color" [ngModel]="level.color" (ngModelChange)="setLevel(i, { color: $event })" [attr.aria-label]="'Colour of block ' + (i + 1)" />
      <input type="text" class="zsd-level-label" [ngModel]="level.label" (ngModelChange)="setLevel(i, { label: $event })" placeholder="Label, e.g. Level 2 — Director" />
      <button type="button" class="ddp-icon" (click)="moveLevel(i, -1)" [disabled]="first" aria-label="Move up"><mat-icon>arrow_upward</mat-icon></button>
      <button type="button" class="ddp-icon" (click)="moveLevel(i, 1)" [disabled]="last" aria-label="Move down"><mat-icon>arrow_downward</mat-icon></button>
      <button type="button" class="ddp-icon" (click)="removeLevel(i)" aria-label="Remove block"><mat-icon>close</mat-icon></button>
    </div>
    <div class="zsd-row">
      <label>From <small>{{ first ? 'empty = start of track' : 'empty = end of previous block' }}</small></label>
      <div class="zsd-row-pickers">
        <ng-select #lvlFrom class="ss-select" [class.is-open]="!!lvlFrom.isOpen()" appendTo="body"
          [items]="columnsOf(binding().dataset)" [searchable]="true" [clearable]="true" [disabled]="!binding().dataset"
          [placeholder]="columnPlaceholder(binding().dataset)"
          [ngModel]="level.fromField || null" (ngModelChange)="setLevel(i, { fromField: $event || '' })"></ng-select>
        <ng-select #lvlFromAgg class="ss-select" [class.is-open]="!!lvlFromAgg.isOpen()" appendTo="body"
          [items]="data.aggregations()" bindLabel="label" bindValue="value" [clearable]="false" [searchable]="false"
          [disabled]="!level.fromField"
          [ngModel]="level.fromAggregation" (ngModelChange)="setLevel(i, { fromAggregation: $event })"></ng-select>
      </div>
    </div>
    <div class="zsd-row">
      <label>To <small>{{ last ? 'empty = end of track' : '' }}</small></label>
      <div class="zsd-row-pickers">
        <ng-select #lvlTo class="ss-select" [class.is-open]="!!lvlTo.isOpen()" appendTo="body"
          [items]="columnsOf(binding().dataset)" [searchable]="true" [clearable]="true" [disabled]="!binding().dataset"
          [placeholder]="columnPlaceholder(binding().dataset)"
          [ngModel]="level.field || null" (ngModelChange)="setLevel(i, { field: $event || '' })"></ng-select>
        <ng-select #lvlToAgg class="ss-select" [class.is-open]="!!lvlToAgg.isOpen()" appendTo="body"
          [items]="data.aggregations()" bindLabel="label" bindValue="value" [clearable]="false" [searchable]="false"
          [disabled]="!level.field"
          [ngModel]="level.aggregation" (ngModelChange)="setLevel(i, { aggregation: $event })"></ng-select>
      </div>
    </div>
  </div>
  <button type="button" class="zsd-add-level" (click)="addLevel()">+ Add block</button>
  <ul class="zsd-warnings" *ngIf="blockWarnings().length">
    <li *ngFor="let warning of blockWarnings()">{{ warning }}</li>
  </ul>
  <p class="ddp-hint">
    Each block is coloured from its <b>From</b> value to its <b>To</b> value (e.g. Floor → Target). Tick
    <b>Show the value at every level boundary</b> in the Design tab to print every value under the track.
  </p>
</ng-container>
<ng-template #otherZones>
  <p class="ddp-hint">
    The blocks now come from {{ binding().zonesMode === 'rows' ? 'dataset rows (see Advanced)' : 'the zone list in the Design tab' }}.
  </p>
  <button type="button" class="zsd-add-level" (click)="setZonesMode('columns')">Take the blocks from columns</button>
</ng-template>

<!-- ── Track start / end: like Set Price's Minimum / Maximum value ───────── -->
<div class="zsd-field">
  <label>Minimum value (columns or number)</label>
  <ng-select #minCmp class="ss-select" [class.is-open]="!!minCmp.isOpen()" appendTo="body"
    [items]="columnsOf(binding().dataset)" [multiple]="true" [closeOnSelect]="false" [searchable]="true" [clearable]="true"
    [addTag]="addBoundNumber" addTagText="Use number"
    [disabled]="!binding().dataset" placeholder="e.g. total_cost, current_price"
    [ngModel]="binding().minCompareFields" (ngModelChange)="setCompareFields('minCompareFields', $event)"></ng-select>
</div>
<div class="zsd-field" *ngIf="binding().minCompareFields.length > 1">
  <label>Minimum = the</label>
  <select [ngModel]="binding().minPick || 'lowest'" (ngModelChange)="setField('minPick', $event)">
    <option *ngFor="let option of pickOptions" [value]="option.value">{{ option.label }}</option>
  </select>
</div>
<div class="zsd-field" *ngIf="binding().minCompareFields.length">
  <label>Minimum adjustment (%)</label>
  <input type="number" step="1" [ngModel]="binding().minAdjustPct === '' ? null : +binding().minAdjustPct"
    (ngModelChange)="setAdjustPct('minAdjustPct', $event)" placeholder="e.g. -10" />
</div>
<div class="zsd-field">
  <label>Maximum value (columns or number)</label>
  <ng-select #maxCmp class="ss-select" [class.is-open]="!!maxCmp.isOpen()" appendTo="body"
    [items]="columnsOf(binding().dataset)" [multiple]="true" [closeOnSelect]="false" [searchable]="true" [clearable]="true"
    [addTag]="addBoundNumber" addTagText="Use number"
    [disabled]="!binding().dataset" placeholder="e.g. target_cost, current_price"
    [ngModel]="binding().maxCompareFields" (ngModelChange)="setCompareFields('maxCompareFields', $event)"></ng-select>
</div>
<div class="zsd-field" *ngIf="binding().maxCompareFields.length > 1">
  <label>Maximum = the</label>
  <select [ngModel]="binding().maxPick || 'highest'" (ngModelChange)="setField('maxPick', $event)">
    <option *ngFor="let option of pickOptions" [value]="option.value">{{ option.label }}</option>
  </select>
</div>
<div class="zsd-field" *ngIf="binding().maxCompareFields.length">
  <label>Maximum adjustment (%)</label>
  <input type="number" step="1" [ngModel]="binding().maxAdjustPct === '' ? null : +binding().maxAdjustPct"
    (ngModelChange)="setAdjustPct('maxAdjustPct', $event)" placeholder="e.g. 10" />
</div>
<p class="ddp-hint">
  The handle can go from the picked minimum (with its %, -10 = 10% below) to the picked maximum (10 = 10% above).
  Nothing picked = the track runs over the blocks with the Design tab's extra room.
</p>

<!-- ── Advanced: fixed range, cost, other zone sources ────────────────────── -->
<button type="button" class="zsd-advanced-toggle" (click)="advancedOpen.set(!advancedOpen())" [attr.aria-expanded]="advancedOpen()">
  <mat-icon>{{ advancedOpen() ? 'expand_less' : 'expand_more' }}</mat-icon> Advanced
</button>
<div class="zsd-advanced" *ngIf="advancedOpen()">
  <p class="ddp-hint">Without these, the track runs from the first block's start to the last block's end.</p>
  <div class="zsd-row">
      <label>Minimum <small>left end of the track</small></label>
      <div class="zsd-row-pickers">
        <ng-select #minCol class="ss-select" [class.is-open]="!!minCol.isOpen()" appendTo="body"
          [items]="columnsOf(binding().dataset)" [searchable]="true" [clearable]="true" [disabled]="!binding().dataset"
          [placeholder]="columnPlaceholder(binding().dataset)"
          [ngModel]="binding().minField || null" (ngModelChange)="setField('minField', $event)"></ng-select>
        <ng-select #minAgg class="ss-select" [class.is-open]="!!minAgg.isOpen()" appendTo="body"
          [items]="data.aggregations()" bindLabel="label" bindValue="value" [clearable]="false" [searchable]="false"
          [disabled]="!binding().minField"
          [ngModel]="binding().minAggregation" (ngModelChange)="setField('minAggregation', $event)"></ng-select>
      </div>
    </div>
  <div class="zsd-row">
      <label>Maximum <small>right end of the track</small></label>
      <div class="zsd-row-pickers">
        <ng-select #maxCol class="ss-select" [class.is-open]="!!maxCol.isOpen()" appendTo="body"
          [items]="columnsOf(binding().dataset)" [searchable]="true" [clearable]="true" [disabled]="!binding().dataset"
          [placeholder]="columnPlaceholder(binding().dataset)"
          [ngModel]="binding().maxField || null" (ngModelChange)="setField('maxField', $event)"></ng-select>
        <ng-select #maxAgg class="ss-select" [class.is-open]="!!maxAgg.isOpen()" appendTo="body"
          [items]="data.aggregations()" bindLabel="label" bindValue="value" [clearable]="false" [searchable]="false"
          [disabled]="!binding().maxField"
          [ngModel]="binding().maxAggregation" (ngModelChange)="setField('maxAggregation', $event)"></ng-select>
      </div>
    </div>
  <div class="zsd-row">
      <label>Cost basis <small>for the &#123;margin&#125; token</small></label>
      <div class="zsd-row-pickers">
        <ng-select #costCol class="ss-select" [class.is-open]="!!costCol.isOpen()" appendTo="body"
          [items]="columnsOf(binding().dataset)" [searchable]="true" [clearable]="true" [disabled]="!binding().dataset"
          [placeholder]="columnPlaceholder(binding().dataset)"
          [ngModel]="binding().costField || null" (ngModelChange)="setField('costField', $event)"></ng-select>
        <ng-select #costAgg class="ss-select" [class.is-open]="!!costAgg.isOpen()" appendTo="body"
          [items]="data.aggregations()" bindLabel="label" bindValue="value" [clearable]="false" [searchable]="false"
          [disabled]="!binding().costField"
          [ngModel]="binding().costAggregation" (ngModelChange)="setField('costAggregation', $event)"></ng-select>
      </div>
    </div>

  <label class="zsd-sub">Where the blocks come from</label>
  <div class="zsd-mode" role="radiogroup" aria-label="Where the blocks come from">
    <button type="button" [class.is-active]="binding().zonesMode === 'columns'" (click)="setZonesMode('columns')">Columns</button>
    <button type="button" [class.is-active]="binding().zonesMode === 'static'" (click)="setZonesMode('static')">Design tab list</button>
    <button type="button" [class.is-active]="binding().zonesMode === 'rows'" (click)="setZonesMode('rows')">Dataset rows</button>
  </div>

  <ng-container *ngIf="binding().zonesMode === 'rows'">
    <p class="ddp-hint">Every row becomes one coloured band. Pick the columns that hold its label, bounds and (optionally) colour.</p>
    <div class="ddp-field">
      <label>Zones dataset <small>empty = same as above</small></label>
      <ng-select #zdsSelect class="ss-select" [class.is-open]="!!zdsSelect.isOpen()" appendTo="body"
        [items]="datasetOptions()" [searchable]="true" [clearable]="true"
        [placeholder]="binding().dataset ? 'Same as ' + binding().dataset : 'Select a dataset'"
        [ngModel]="binding().zonesDataset || null" (ngModelChange)="setZonesDataset($event)"></ng-select>
      <p class="ddp-status is-error" *ngIf="columnsError(zonesDataset()) as message">{{ message }}</p>
    </div>
    <div class="ddp-field">
      <label>Label column</label>
      <ng-select #zlSelect class="ss-select" [class.is-open]="!!zlSelect.isOpen()" appendTo="body"
        [items]="columnsOf(zonesDataset())" [searchable]="true" [clearable]="true" [disabled]="!zonesDataset()"
        [placeholder]="columnPlaceholder(zonesDataset())"
        [ngModel]="binding().zoneLabelField || null" (ngModelChange)="setField('zoneLabelField', $event)"></ng-select>
    </div>
    <div class="ddp-field">
      <label>From column <small>optional</small></label>
      <ng-select #zfSelect class="ss-select" [class.is-open]="!!zfSelect.isOpen()" appendTo="body"
        [items]="columnsOf(zonesDataset())" [searchable]="true" [clearable]="true" [disabled]="!zonesDataset()"
        [placeholder]="columnPlaceholder(zonesDataset())"
        [ngModel]="binding().zoneFromField || null" (ngModelChange)="setField('zoneFromField', $event)"></ng-select>
    </div>
    <div class="ddp-field">
      <label>To column <small>required</small></label>
      <ng-select #ztSelect class="ss-select" [class.is-open]="!!ztSelect.isOpen()" appendTo="body"
        [items]="columnsOf(zonesDataset())" [searchable]="true" [clearable]="true" [disabled]="!zonesDataset()"
        [placeholder]="columnPlaceholder(zonesDataset())"
        [ngModel]="binding().zoneToField || null" (ngModelChange)="setField('zoneToField', $event)"></ng-select>
    </div>
    <div class="ddp-field">
      <label>Colour column <small>optional — any CSS colour; missing → palette</small></label>
      <ng-select #zcSelect class="ss-select" [class.is-open]="!!zcSelect.isOpen()" appendTo="body"
        [items]="columnsOf(zonesDataset())" [searchable]="true" [clearable]="true" [disabled]="!zonesDataset()"
        [placeholder]="columnPlaceholder(zonesDataset())"
        [ngModel]="binding().zoneColorField || null" (ngModelChange)="setField('zoneColorField', $event)"></ng-select>
    </div>
  </ng-container>
</div>

<!-- ── Generate: register the mapped measures with the compute API ───────── -->
<div class="zsd-generate-row">
  <!-- The span carries the tooltip: a disabled button swallows pointer events. -->
  <span class="zsd-generate-wrap" [title]="generateBlockerText()">
    <button type="button" class="zsd-primary" [disabled]="!canGenerate()" (click)="generateConfiguration()">
      {{ generateLabel() }}
    </button>
  </span>
  <div class="zsd-missing" *ngIf="!canGenerate() && !isGenerating() && generateBlockers().length">
    <strong>Still needed:</strong>
    <ul>
      <li *ngFor="let item of generateBlockers()">{{ item }}</li>
    </ul>
  </div>
</div>

<!-- ── Preview ────────────────────────────────────────────────────────────── -->
<section class="ddp-preview" *ngIf="preview() as model">
  <div class="zsd-preview-row">
    <div class="ddp-preview-value">
      <small>Value</small>
      <strong>{{ model.valueText }}</strong>
    </div>
    <div class="ddp-preview-value">
      <small>Range</small>
      <strong class="is-small">{{ model.minText }} – {{ model.maxText }}</strong>
    </div>
  </div>
  <div class="zsd-zones" *ngIf="model.zones.length">
    <small>Zones</small>
    <span class="zsd-zone-chip" *ngFor="let zone of model.zones; trackBy: trackByIndex" [class.is-active]="zone.active">
      <i [style.background]="zone.color"></i>
      {{ zone.label || 'Zone' }}
      <em>{{ zone.from | number:'1.0-2' }}–{{ zone.to | number:'1.0-2' }}</em>
    </span>
  </div>
  <p class="ddp-status" [class.is-error]="previewStatus().error">{{ previewStatus().text }}</p>
</section>

<button type="button" class="ddp-unlink" *ngIf="isBound()" (click)="unbind()">
  <mat-icon>link_off</mat-icon> Unlink from data
</button>
